import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ReactComponent as DropdownArrow } from "../../assets/icons/dropdownArrow.svg";
import {
  collectionBuildBorderClassName,
  collectionBuildDividerClassName,
} from "./collectionBuildConstants";

export type DenimSwatchOption = {
  id: string;
  label: string;
  previewUrl?: string;
};

type Props = {
  value: string;
  options: DenimSwatchOption[];
  placeholder?: string;
  onChange: (next: string) => void;
  /** Portrait material tiles vs square wash tiles (Figma). */
  variant: "materials" | "washes";
};

type MenuRect = {
  top: number;
  left: number;
  width: number;
};

const triggerClassName = `box-border h-10 rounded-lg ${collectionBuildBorderClassName} pl-3.5 pr-8 text-sm`;

const CheckboxMark: React.FC = () => (
  <span className="text-[9px] leading-none" aria-hidden="true">
    ✓
  </span>
);

/**
 * Denim Look Details Materials / Washes single-select with visual swatches.
 * Matches Figma tile layout; one option selected at a time (overall section parity).
 */
const CollectionBuildDenimSwatchSelect: React.FC<Props> = ({
  value,
  options,
  placeholder = "Select",
  onChange,
  variant,
}) => {
  const [open, setOpen] = useState(false);
  const [menuRect, setMenuRect] = useState<MenuRect | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const selectedLabel =
    options.find(option => option.id === value)?.label || "";

  useLayoutEffect(() => {
    if (!open) {
      setMenuRect(null);
      return;
    }
    const updatePosition = () => {
      const trigger = buttonRef.current;
      if (!trigger) {
        return;
      }
      const rect = trigger.getBoundingClientRect();
      const preferredWidth =
        variant === "materials"
          ? Math.max(rect.width, 420)
          : Math.max(rect.width, 360);
      const maxLeft = Math.max(8, window.innerWidth - preferredWidth - 8);
      setMenuRect({
        top: rect.bottom + 4,
        left: Math.min(rect.left, maxLeft),
        width: preferredWidth,
      });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    document.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, variant]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className="relative min-w-0 w-full overflow-visible" ref={triggerRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(current => !current)}
        className={`relative flex w-full min-w-0 items-center border bg-white text-left focus:outline-none ${triggerClassName} ${
          selectedLabel ? "text-secondary" : "text-tertiary"
        }`}
      >
        <span className="min-w-0 flex-1 truncate">
          {selectedLabel || placeholder}
        </span>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <span
            className={`transition-transform duration-200 ${
              open ? "rotate-180" : ""
            }`}
          >
            <DropdownArrow className="h-2 w-2 text-secondary" />
          </span>
        </span>
      </button>
      {open &&
        menuRect &&
        createPortal(
          <div
            ref={menuRef}
            className={`fixed z-50 max-h-[22rem] overflow-y-auto rounded-xl border ${collectionBuildBorderClassName} bg-white p-3 shadow-[0_8px_24px_rgba(0,0,0,0.12)]`}
            style={{
              top: menuRect.top,
              left: menuRect.left,
              width: menuRect.width,
            }}
          >
            <div
              className={
                variant === "materials"
                  ? "grid grid-cols-4 gap-2.5"
                  : "grid grid-cols-4 gap-2"
              }
            >
              {options.map(option => {
                const checked = option.id === value;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => {
                      onChange(checked ? "" : option.id);
                      setOpen(false);
                    }}
                    aria-pressed={checked}
                    className="group min-w-0 text-left focus:outline-none"
                  >
                    <span
                      className={`relative block w-full overflow-hidden rounded-lg border bg-[#F3F1EE] ${
                        variant === "materials"
                          ? "aspect-[2/3]"
                          : "aspect-square"
                      } ${
                        checked
                          ? "border-secondary ring-2 ring-secondary ring-offset-1"
                          : `${collectionBuildBorderClassName} group-hover:border-secondary`
                      }`}
                    >
                      {option.previewUrl ? (
                        <img
                          src={option.previewUrl}
                          alt=""
                          className={`h-full w-full ${
                            variant === "materials"
                              ? "object-contain"
                              : "object-cover"
                          }`}
                        />
                      ) : (
                        <span className="absolute inset-0 bg-[#EDE8E3]" />
                      )}
                      <span
                        className={`absolute left-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-[3px] border ${
                          checked
                            ? "border-[#1A1A1A] bg-[#1A1A1A] text-white"
                            : "border-[#D0C8C0] bg-white"
                        }`}
                      >
                        {checked ? <CheckboxMark /> : null}
                      </span>
                    </span>
                    <span
                      className={`mt-1.5 block truncate text-center text-[11px] leading-tight text-secondary ${
                        checked ? "font-medium" : ""
                      }`}
                    >
                      {option.label}
                    </span>
                  </button>
                );
              })}
            </div>
            {!options.length ? (
              <p
                className={`border-t ${collectionBuildDividerClassName} pt-2 text-sm text-tertiary`}
              >
                No denim {variant} in the catalog yet.
              </p>
            ) : null}
          </div>,
          document.body
        )}
    </div>
  );
};

export default CollectionBuildDenimSwatchSelect;
